<template>
  <div ref="form">
    <mod-form-presentation
      :website-details="websiteDetails"
      :pages="form.pages"
      :error-msg="errorMsg"
      :loading="loading"
      :submitted="submitted"
      :delay-touch="delayTouch"
      :set-page-nav-requires-confirmation="setPageNavRequiresConfirmation"
      :add-page="addPage"
      :delete-page="deletePage"
      :handle-submit="handleSubmit"
    />
  </div>
</template>

<script>
import GET_WEBSITE_DETAILS from '@/graphql/GET_WEBSITE_DETAILS.gql'
import MODIFY_WEBSITE from '@/graphql/MODIFY_WEBSITE.gql'
import { SECONDS_IN_MS } from '@/helpers'
import { mapActions, mapState } from 'vuex'
import ModFormPresentation from './ModFormPresentation'
import { TimelineLite } from 'gsap'
import { dropBackwards, flyUpOffScreen, reset } from '@/gsap-animations'

const touchMap = new WeakMap()

export default {

  name: 'ModForm',
  components: {
    ModFormPresentation
  },

  data () {
    return {
      websiteDetails: { pages: [] },
      disableDelayedTouch: false,
      submitted: false,
      loading: false,
      pageChangeCount: 0,
      form: {
        website: null,
        pages: [{
          identifier: 0,
          name: '',
          change: null,
          files: []
        }]
      }
    }
  },

  computed: {
    ...mapState(['services', 'account']),

    websiteId () {
      return this.services.website
    },

    // Create an object with keys that are page names, and values that are the
    // page ID (if the page already existed) or null if the page is new.
    nameToIdMap () {
      const pages = this.websiteDetails.pages
      const idMap = {}
      for (let i = 0; i < pages.length; i++) {
        const upperCasedPageName = pages[i].name && pages[i].name.toUpperCase().trim()
        if (!Object.hasOwnProperty.call(idMap, upperCasedPageName)) {
          idMap[upperCasedPageName] = pages[i].id
        }
      }
      return idMap
    },

    // format the data into a shape ready for the graphql mutation
    completedForm () {
      const pages = this.form.pages.map(({ name, change, files }) => ({
        pageId: this.nameToIdMap[name.toUpperCase().trim()] || null,
        name,
        change,
        files
      }))

      return {
        input: {
          customerId: this.account.customerId,
          accountId: this.account.accountId,
          // accountHasPPC: Object.hasOwnProperty.call(this.services, 'payPerClick'),
          websiteId: this.services.website,
          pages
        }
      }
    },

    // determine the error message to show depending on the result of the validation of a field
    errorMsg () {
      return (field) => {
        if (field.$error) {
          if (!field.required) {
            return this.$t('validation.required')
          }
          if (!field.minLength) {
            return this.$t('validation.min_length', { num: field.$params.minLength.min })
          }
          if (!field.maxLength) {
            return this.$t('validation.max_length', { num: field.$params.maxLength.min })
          }
        }
      }
    }
  },

  mounted () {
    // scroll to the top of the page when a new form is mounted
    document.getElementsByTagName('body')[0].scrollTo(0, 0)
  },

  beforeDestroy () {
    // perform clean up to ensure no timers or handlers are left hanging
    clearInterval(this.timer)
  },

  methods: {
    ...mapActions(['setPageNavRequiresConfirmation']),
    // Adds a delay between a keypress and the validation process to give the user a chance to
    // finish typing before they are alerted to validation errors of the input field
    delayTouch (validator, disableValidation) {
      if (disableValidation) return validator.$reset()
      if (this.disableDelayedTouch) return
      validator.$reset()
      if (touchMap.has(validator)) {
        clearTimeout(touchMap.get(validator))
      }
      touchMap.set(validator, setTimeout(validator.$touch, SECONDS_IN_MS(1)))
    },

    // Scroll to the first input field that has a validation error
    scrollPageToInvalidInput () {
      this.$nextTick(() => {
        this.$scrollTo('.is-danger', SECONDS_IN_MS(1), {
          offset: -300
        })
      })
    },

    async handleSubmit (validator) {
      // use $ in variables to indicate a DOM reference
      const $form = this.$refs.form
      validator.$reset()
      validator.$touch()
      this.disableDelayedTouch = true

      // If a field is invalid, scroll the page to that field and exit the handleSubmit
      // method early
      if (validator.$invalid) {
        this.scrollPageToInvalidInput()
        return
      }
      this.submitted = true

      // This callback will be called when the greensock timeline instance completes, ie,
      // when the form submission animation has finished
      const tlCallback = () => {
        this.resetForm()
        // A callback is sent with the modal event so that when the submission
        // confirmation has been acknowledged in the modal, it can be called to
        // reset the form, ready for further submissions if necessary
        this.$root.$emit('show::custom::modal', 'form-submitted', () => reset($form))
        this.submitted = false
      }

      // create a new greensock timeline and add animations to perform to it
      const tl = new TimelineLite()
      tl.add(dropBackwards($form))

      try {
        // submit the form data to the graphql API and wair for a response
        await this.$apollo.mutate({
          mutation: MODIFY_WEBSITE,
          variables: this.completedForm
        })

        // Once successfully submitted, the form can animate offscreen as if being
        // posted, then pause, and then call the callback to show a modal
        // to inform the user the submission was successful
        tl.add(flyUpOffScreen($form), '+=0.8')
        tl.addPause('+=0.3')
        tl.eventCallback('onComplete', tlCallback)
        this.$nextTick(() => {
          validator.$reset()
          this.disableDelayedTouch = false
          this.$store.dispatch('setPageNavRequiresConfirmation', false)
        })

      } catch (err) {
        console.error(err)
        // if submission failed, reverse the animation of the form, to bring
        // it back into view
        tl.reverse()
        this.submitted = false
      }
    },

    // A bit awkward, but it works. Reset the state of this component ready for further use
    resetForm () {
      this.form.pages[0].change = ''
      this.draggedOverIndex = ''
      this.pageCount = 0
      this.form.website = null
      this.form.pages = [{ identifier: 0, name: '', change: '', files: [] }]
    },

    // This logic exists because previously there was an 'Add another change' button on each
    // section, and this enabled a section to be inserted immediately under the section initiating
    // the creation. Overcomplex for the current use, but works.
    addPage (index, creatorId) {
      this.pageChangeCount++
      this.form.pages = [
        ...this.form.pages.slice(0, index + 1),
        {
          identifier: this.pageChangeCount,
          name: '',
          change: '',
          files: [],
          warning: ''
        },
        ...this.form.pages.slice(index + 1)
      ]

      // scroll to the location of the newly created section
      this.$scrollTo(`#file-${creatorId}-panel`, SECONDS_IN_MS(1))
    },

    async deletePage (identifier, event) {
      // prevent a double-click on the delete from deleting ALL page-modification panels
      if (this.form.pages.length <= 1) return

      const [pageToBeDeleted] = this.form.pages.filter(page => page.identifier === identifier)

      // we need to remove any files attached to the page from our S3 storage
      const filesToRemoveFromAWS = pageToBeDeleted.files.map(file =>
        this.$s3.remove(this.pathToFile(file))
      )

      await Promise.all(filesToRemoveFromAWS)

      this.form.pages = this.form.pages.filter(page => page.identifier !== identifier)
    },

    pathToFile (file) {
      return `${file.path}/${file.filename}`
    }

  },

  apollo: {
    websiteDetails: {
      query: GET_WEBSITE_DETAILS,
      variables () {
        return { websiteId: this.websiteId }
      },
      update: data => {
        return data.getWebsiteDetails
      }
    }
  }
}
</script>
